<template>
    <b-modal v-model="modalOpen" :width="800" :can-cancel="true" :backdrop="true">
        <div class="modal-card" style="width: auto;overflow: auto;">
            <header class="modal-card-head">
                <p class="modal-card-title">Korting toepassen</p>
            </header>

            <section class="modal-card-body" style="overflow: hidden;border-radius:0;">
                <!-- Preset Selection -->
                <b-field label="Selecteer kortingspreset">
                    <b-select
                        v-model="selectedPresetId"
                        placeholder="Kies een preset of maak hieronder een aangepaste korting"
                        expanded
                        @input="onPresetSelected"
                    >
                        <option :value="null">-- Geen preset (aangepast) --</option>
                        <option
                            v-for="preset in presets"
                            :key="preset.id"
                            :value="preset.id"
                        >
                            {{ preset.name }}{{ preset.description ? ' - ' + preset.description : '' }}
                        </option>
                    </b-select>
                </b-field>

                <hr>

                <!-- Use InvoiceDiscountManager component -->
                <invoice-discount-manager
                    :discounts.sync="discounts"
                    :apply-to-all.sync="applyToAllInvoices"
                    :product-price="productPrice"
                    :disabled="selectedPresetId !== null"
                    :show-description="true"
                    :show-help-text="restrictToAllInvoices ? true : !singleInvoiceMode"
                    :max-discounts="(singleInvoiceMode || restrictToAllInvoices) ? 1 : 12"
                />

                <!-- OLD: Discount list - keeping this temporarily for reference -->
                <div v-for="(discount, index) in discounts" :key="index" class="discount-item" v-if="false">
                    <div class="box">
                        <div class="row">
                            <div class="column is-6">
                                <h4>{{ getInvoiceLabel(index) }}</h4>
                            </div>
                            <div class="column is-6 text-right">
                                <b-button
                                    type="is-danger"
                                    icon-left="trash-alt"
                                    @click="removeDiscount(index)"
                                    :disabled="discounts.length === 1 || selectedPresetId !== null"
                                />
                            </div>
                        </div>

                        <!-- Apply to all toggle - only show for first discount when there's only one -->
                        <div class="row" v-if="index === 0 && discounts.length === 1 && selectedPresetId === null">
                            <div class="column">
                                <b-field>
                                    <b-radio v-model="applyToAllInvoices" :native-value="true">
                                        Toepassen op alle facturen
                                    </b-radio>
                                </b-field>
                                <b-field>
                                    <b-radio v-model="applyToAllInvoices" :native-value="false">
                                        Alleen toepassen op de eerste factuur
                                    </b-radio>
                                </b-field>
                            </div>
                        </div>

                        <div class="row">
                            <div class="column is-6">
                                <b-field label="Type">
                                    <b-select v-model="discount.type" expanded :disabled="selectedPresetId !== null">
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Vast bedrag</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column is-6">
                                <b-field :label="discount.type === 'percentage' ? 'Percentage' : 'Bedrag (€)'">
                                    <b-input
                                        v-model.number="discount.value"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        :placeholder="discount.type === 'percentage' ? '10' : '5.00'"
                                        :disabled="selectedPresetId !== null"
                                    >
                                        <template #prepend>
                                            <b-icon :icon="discount.type === 'percentage' ? 'percent' : 'euro-sign'"></b-icon>
                                        </template>
                                    </b-input>
                                </b-field>
                            </div>
                            <div class="column is-12">
                                <b-field label="Omschrijving">
                                    <b-input
                                        v-model="discount.description"
                                        placeholder="Optioneel"
                                        :disabled="selectedPresetId !== null"
                                    ></b-input>
                                </b-field>
                            </div>
                        </div>

                        <!-- Calculation for this discount -->
                        <div class="row" v-if="discount.value > 0">
                            <div class="column">
                                <b-notification type="is-light" size="is-small">
                                    <strong>Berekening:</strong>
                                    Productprijs: €{{ productPrice.toFixed(2).replace('.', ',') }}
                                    - Korting: {{ getDiscountDisplay(discount) }}
                                    ({{ getDiscountAmount(discount) }})
                                    = <strong>€{{ calculateDiscountedPrice(discount).toFixed(2).replace('.', ',') }}</strong>
                                </b-notification>
                            </div>
                        </div>

                    </div>
                </div>

            </section>

            <footer class="modal-card-foot">
                <b-button
                    v-if="hasExistingDiscounts"
                    type="is-danger"
                    outlined
                    @click="removeAllDiscounts"
                >
                    Verwijderen
                </b-button>
                <div class="is-flex-grow-1"></div>
                <b-button @click="$emit('close')">Annuleren</b-button>
                <b-button
                    type="is-primary"
                    @click="applyDiscounts"
                >
                    Toepassen
                </b-button>
            </footer>
        </div>
    </b-modal>
</template>

<script>
import InvoiceDiscountManager from './components/InvoiceDiscountManager.vue';
import axios from 'axios';

export default {
    name: 'DiscountModal',

    components: {
        InvoiceDiscountManager
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true
        },
        productPrice: {
            type: Number,
            default: 0
        },
        existingDiscounts: {
            type: Array,
            default: () => []
        },
        singleInvoiceMode: {
            type: Boolean,
            default: false
        },
        restrictToAllInvoices: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            presets: [],
            loadingPresets: false,
            selectedPresetId: null,
            discounts: [this.createEmptyDiscount()],
            applyToAllInvoices: true // Default: apply to all invoices
        };
    },

    computed: {
        hasExistingDiscounts() {
            return this.existingDiscounts && this.existingDiscounts.length > 0;
        },

        hasValidDiscounts() {
            return this.discounts.some(d => d.value > 0);
        },

        modalOpen: {
            get() {
                return this.isOpen
            },
            set(value) {
                if (!value) {
                    this.$emit('close')
                }
            }
        }
    },

    watch: {
        isOpen(val) {
            if (val) {
                this.loadPresets();
                this.initializeFromExisting()
            }
        },

        'discounts.length'(newLength) {
            if (this.restrictToAllInvoices) {
                this.applyToAllInvoices = true
                if (newLength > 1) {
                    this.discounts = [this.discounts[0]]
                }
                return
            }

            // Disable applyToAll when multiple discounts are added
            if (newLength > 1) {
                this.applyToAllInvoices = false
            }
        }
    },

    methods: {
        createEmptyDiscount() {
            return {
                type: 'percentage',
                value: 0,
                description: '',
                invoice_number: null
            };
        },

        async loadPresets() {
            this.loadingPresets = true;

            try {
                const headers = { 'Authorization': 'bearer ' + this.$store.state.authToken };
                const response = await axios.get('/api/discounts/active', { headers })
                this.presets = response.data
            } catch (error) {
                console.error('Error loading discount presets:', error);
                // Don't show error toast - presets are optional
            } finally {
                this.loadingPresets = false
            }
        },

        initializeFromExisting() {
            if (this.existingDiscounts && this.existingDiscounts.length > 0) {
                this.selectedPresetId = this.existingDiscounts[0].discount_preset || null;
                this.discounts = this.restrictToAllInvoices
                    ? [{
                        type: this.existingDiscounts[0].type,
                        value: parseFloat(this.existingDiscounts[0].value),
                        description: this.existingDiscounts[0].description || '',
                        invoice_number: null,
                        discount_preset: this.existingDiscounts[0].discount_preset
                    }]
                    : this.existingDiscounts.map(d => ({
                        type: d.type,
                        value: parseFloat(d.value),
                        description: d.description || '',
                        invoice_number: d.invoice_number,
                        discount_preset: d.discount_preset
                    }));

                this.applyToAllInvoices = this.restrictToAllInvoices
                    ? true
                    : (this.existingDiscounts.length === 1
                        ? (this.existingDiscounts[0].apply_to_all || this.existingDiscounts[0].invoice_number === null)
                        : false);
            } else {
                this.selectedPresetId = null;
                this.discounts = [this.createEmptyDiscount()];
                this.applyToAllInvoices = true;
            }
        },

        onPresetSelected(presetId) {
            if (!presetId) {
                // Reset to single empty discount
                this.discounts = [this.createEmptyDiscount()]
                return
            }

            const preset = this.presets.find(p => p.id === presetId)
            if (!preset || !preset.discounts || preset.discounts.length === 0) {
                this.$buefy.toast.open({
                    message: 'Deze preset heeft geen kortingen geconfigureerd',
                    type: 'is-warning'
                });
                this.selectedPresetId = null
                return
            }

            // Load all invoice discounts from the preset or just the first one
            // when single invoice/addendum-all-invoices mode is active.
            if (this.singleInvoiceMode || this.restrictToAllInvoices) {
                this.discounts = [{
                    type: preset.discounts[0].type,
                    value: parseFloat(preset.discounts[0].value),
                    description: preset.name,
                    discount_preset: presetId,
                    invoice_number: null
                }]
            } else {
                this.discounts = preset.discounts.map((discount, index) => ({
                    type: discount.type,
                    value: parseFloat(discount.value),
                    description: preset.name,
                    discount_preset: presetId,
                    invoice_number: preset.discounts.length === 1 ? null : (index + 1)
                }))
            }

            this.applyToAllInvoices = this.restrictToAllInvoices
                ? true
                : (preset.discounts.length === 1)
        },

        applyDiscounts() {
            // Don't filter out 0% discounts - they're valid
            const discountsData = this.discounts
                .map((discount, index) => ({
                    discount_preset: this.selectedPresetId,
                    type: discount.type,
                    value: discount.value,
                    description: discount.description || null,
                    invoice: this.restrictToAllInvoices
                        ? null
                        : (this.discounts.length === 1 && this.applyToAllInvoices ? null : (index + 1)),
                    apply_to_all: this.restrictToAllInvoices
                        ? true
                        : (this.discounts.length === 1 && this.applyToAllInvoices)
                }));

            if (discountsData.length === 0) {
                this.$buefy.toast.open({
                    message: 'Voer minimaal één korting in',
                    type: 'is-warning'
                });
                return;
            }

            // Emit in format expected by both contract and loose invoice flows
            this.$emit('apply', {
                discounts: discountsData,
                applyToAll: this.restrictToAllInvoices
                    ? true
                    : (this.discounts.length === 1 && this.applyToAllInvoices)
            });
            this.$emit('close');
        },

        removeAllDiscounts() {
            this.$emit('remove');
            this.$emit('close');
        }
    }
};
</script>
